'use client';

import * as Headless from '@headlessui/react';
import { normalizeCatalogHubPath } from '@repo/config';
import { resolveChromeHref } from '@repo/web-chrome/chrome-config/resolve-chrome-href';
import { useOptionalChromeConfig } from '@repo/web-chrome/chrome-config/use-optional-chrome-config';
import { isEnglishOnlyPrivatePath } from '@repo/web-chrome/lib/i18n/english-only-private-paths';
import { LocaleLink } from '@repo/web-chrome/lib/i18n/navigation';
import { forwardRef } from 'react';

type LinkPropsType = React.ComponentProps<typeof LocaleLink>;

export const Link: React.ForwardRefExoticComponent<
  LinkPropsType & React.RefAttributes<HTMLAnchorElement>
> = forwardRef(function Link(
  props: LinkPropsType,
  ref: React.ForwardedRef<HTMLAnchorElement>,
): React.JSX.Element {
  const config = useOptionalChromeConfig();
  const href = typeof props.href === 'string' ? props.href : props.href?.pathname;
  const crossOriginHref = config && href ? resolveChromeHref(config, href) : null;
  if (crossOriginHref) {
    const {
      href: _ignoredHref,
      locale: _ignoredLocale,
      prefetch: _ignoredPrefetch,
      ...rest
    } = props;
    return (
      <Headless.DataInteractive>
        <a ref={ref} href={crossOriginHref} {...rest} />
      </Headless.DataInteractive>
    );
  }
  const isPrivate = Boolean(href && isEnglishOnlyPrivatePath(href));
  const forcedLocale = isPrivate ? 'en' : props.locale;
  const prefetch = isPrivate ? false : props.prefetch;
  const localHref =
    config?.isCatalogApp && typeof props.href === 'string'
      ? normalizeCatalogHubPath(props.href)
      : props.href;
  return (
    <Headless.DataInteractive>
      <LocaleLink ref={ref} {...props} href={localHref} locale={forcedLocale} prefetch={prefetch} />
    </Headless.DataInteractive>
  );
});
